import {
    AnalyticsEvent,
    generateInsertId,
    PUSH_NOTIFICATION_FAILURE,
    PUSH_NOTIFICATION_SUCCESS,
} from '../common/Analytics';
import { ErrorReporter } from '../common/ErrorReporter';
import { APNRawPayload, PushNotificationResponse } from '../common/PushNotifications';
import DB from '../db/DB';
import { ReplicantError, ReplicantErrorCode, ReplicantErrorSubCode } from '../Errors';
import { Replicant } from '../ReplicantConfig';
import { PostAPNFn } from './ServerReplicantAPN';

export type SendIOSPushNotificationFn = (opts: {
    receiverId: string;
    appleDeviceToken: string;
    rawPayload: APNRawPayload;
    userSessionId: string;
    analyticsEventProperties: { [key: string]: unknown };
    analyticsUserProperties?: { [key: string]: unknown };
}) => Promise<PushNotificationResponse>;

export default class ServerReplicantIOSPushNotifications<T extends Replicant> {
    private db: DB<T['state']>;

    constructor(
        private opts: {
            db: DB<T['state']>;
            postActivePushNotification: PostAPNFn;
            sendAnalyticsEvents: (events: AnalyticsEvent[]) => Promise<void>;
            dateNow: () => number;

            errorReporter?: ErrorReporter;
        },
    ) {
        this.db = opts.db;
    }

    sendIOSPushNotification: SendIOSPushNotificationFn = async (opts) => {
        const {
            receiverId,
            appleDeviceToken,
            rawPayload,
            analyticsEventProperties,
            analyticsUserProperties,
            userSessionId,
        } = opts;
        // Send apple iOS notification.
        let success: boolean;
        let reasonUnsuccessful: string | undefined;

        try {
            const result = await this.opts.postActivePushNotification(appleDeviceToken, rawPayload);
            success = result.success;
            if (success) {
                await this.db.chatbot.updateTimeLastTargetedByChannelMessage(receiverId, 'ios', this.opts.dateNow());
            } else {
                reasonUnsuccessful = result.reason;
            }
        } catch (e: any) {
            success = false;
            reasonUnsuccessful = e.message;

            if (this.opts.errorReporter) {
                await this.opts.errorReporter.captureException(
                    new ReplicantError(
                        reasonUnsuccessful || 'Sending active push iOS notification failed',
                        ReplicantErrorCode.server_error,
                        ReplicantErrorSubCode.push_notification_error,
                        'error',
                    ),
                );
            }
        }

        const analyticsEvents: AnalyticsEvent[] = [];

        if (success) {
            const successAnalyticsEvent: AnalyticsEvent = {
                user_id: receiverId,
                event_type: PUSH_NOTIFICATION_SUCCESS,
                event_properties: {
                    ...analyticsEventProperties,
                    platform: 'IOS_APP',
                },
                user_properties: analyticsUserProperties,
                insert_id: generateInsertId(receiverId, userSessionId),
            };
            analyticsEvents.push(successAnalyticsEvent);
        } else {
            const failureAnalyticsEvent: AnalyticsEvent = {
                user_id: receiverId,
                event_type: PUSH_NOTIFICATION_FAILURE,
                event_properties: {
                    ...analyticsEventProperties,
                    reason: reasonUnsuccessful,
                    platform: 'IOS_APP',
                },
                insert_id: generateInsertId(receiverId, userSessionId),
            };
            analyticsEvents.push(failureAnalyticsEvent);

            // Handle APN unsubs
            if (reasonUnsuccessful === 'Unregistered' || reasonUnsuccessful === 'DeviceTokenNotForTopic') {
                await this.db.chatbot.setAppleDeviceToken(receiverId, '', 0);
            }
        }

        await this.opts.sendAnalyticsEvents(analyticsEvents);

        return { success, reason: reasonUnsuccessful };
    };
}
